import flatMap from "lodash/flatMap"
import subWeeks from "date-fns/subWeeks"
import { endOfWeek } from "date-fns/endOfWeek"
import { getObjectId } from "../utils/utils"
import { getTimesheetHours } from "../comp/DateUtils"
import isSameWeek from "date-fns/isSameWeek"
import { getTimeUtc } from "../utils/dates"
import eachWeekOfInterval from "date-fns/eachWeekOfInterval"
import { toZonedTime } from "date-fns-tz"
import areIntervalsOverlapping from "date-fns/areIntervalsOverlapping"
import { startOfWeek } from "date-fns/startOfWeek"
import { format } from "date-fns"
import { makeHash, isArchived } from "./utils"
import apiTimesheet from "../services/timesheet/timesheet.api"
import { getDbItem } from "../utils/idb-util"

export function validateTimesheets({ timesheets, app, hashRoles }) {
    const missionHash = makeHash(app.state.missions)

    const validTimesheets = []
    const invalidTimesheets = []

    timesheets?.forEach((ts, i) => {
        const stateOrg = app.state.orgs?.find((o) => getObjectId(o) === ts.belongsToOrg)

        if (!stateOrg) {
            return
        }

        if (ts.org) {
            validTimesheets.push(ts)
        } else {
            const stateMission = missionHash[getObjectId(ts?.mission)]

            if (!stateMission && (ts.status === "draft" || ts.status === "rejected")) {
                invalidTimesheets.push(ts)
                return
            }

            const mission = stateMission || hashRoles?.current[ts?.planItemId]?.mission || ts?.snapshot?.mission

            if ((!mission || mission._archived) && ts.status !== "approved") {
                invalidTimesheets.push(ts)
                return
            }

            let pi =
                mission?.planItems?.find((p) => p._id === ts.planItemId) ||
                hashRoles?.current[ts?.planItemId]?.planItem ||
                ts.snapshot?.planItem

            if (!pi) {
                invalidTimesheets.push(ts)
                return
            }

            if (isArchived(pi) && (ts.status === "draft" || ts.status === "rejected")) {
                invalidTimesheets.push(ts)
                return
            }

            if (
                ts.status === "draft" &&
                !areIntervalsOverlapping(
                    {
                        start: toZonedTime(pi.startDate),
                        end: toZonedTime(pi.endDate),
                    },
                    {
                        start: toZonedTime(ts.weekStart),
                        end: toZonedTime(ts.weekEnd),
                    }
                )
            ) {
                invalidTimesheets.push(ts)
                return
            }
            if (pi.inactive && ts.status === "draft") {
                invalidTimesheets.push(ts)
                return
            }

            validTimesheets.push(ts)
        }
    })

    return {
        validTimesheets,
        invalidTimesheets,
    }
}

export function piHasTimesheets(pi, timesheets) {
    if (timesheets.length)
        return timesheets.find((t) => t.planItemId === pi._id && (t.status === "submitted" || t.status === "approved"))

    if (pi.timesheets) {
        return pi.timesheets.length
    }

    return false
}

export async function getMissionTimesheets(mission) {
    const t = await apiTimesheet.getMissionTimesheet(mission?._id)

    return t
}

export function updateStateTimesheets({ timesheets = [], how, data }) {
    let newTimesheets = timesheets.slice()
    switch (how) {
        case "add":
        case "push":
            newTimesheets.push(data)
            break

        case "merge":
            newTimesheets = [...timesheets, ...data]
            break
        case "delete":
            const existinIndex = newTimesheets.findIndex((ts) => ts._id === data._id)
            if (existinIndex !== -1) {
                newTimesheets.splice(existinIndex, 1)
            }
            break

        case "update":
            const existinIndex2 = newTimesheets.findIndex((ts) => ts._id === data._id)

            if (existinIndex2 !== -1) {
                newTimesheets[existinIndex2] = { ...newTimesheets[existinIndex2], ...data }
            }
            break
        case "set":
            newTimesheets = data
            break
        default:
    }

    return newTimesheets
}

export const getMissingTimesheets = async (app) => {
    let timesheets = []

    return []

    const st = startOfWeek(subWeeks(new Date(), 3), { weekStartsOn: 1 })
    const ed = endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 })

    try {
        timesheets = await apiTimesheet.getMyTimesheets({
            weekStart: getTimeUtc(st),
            weekEnd: getTimeUtc(ed),
        })
    } catch (e) {
        app.silentFail("elkrwm3ssio" + e.message)
        return []
    }

    timesheets = timesheets.filter((t) => !t.org || (t.org && getTimesheetHours(t) > 0))

    const roles = flatMap(
        app.state.missions.filter((m) => {
            const orgData = app.state.orgs.find((o) => getObjectId(o) === getObjectId(m.org))
            return !m.noTimesheets && orgData?.isLicensedActive
        }),
        "planItems"
    ).filter((p) => {
        const init =
            p.person === app.state.person._id &&
            !p.inactive &&
            areIntervalsOverlapping(
                {
                    start: st,
                    end: ed,
                },
                {
                    start: toZonedTime(p.startDate),
                    end: toZonedTime(p.endDate),
                }
            )

        return init
    })

    let missing = []

    eachWeekOfInterval(
        {
            start: st,
            end: ed,
        },
        { weekStartsOn: 1 }
    ).forEach((week, i) => {
        roles.forEach((pi, i) => {
            if (
                !areIntervalsOverlapping(
                    {
                        start: week,
                        end: endOfWeek(week, { weekStartsOn: 1 }),
                    },
                    {
                        start: toZonedTime(pi.startDate),
                        end: toZonedTime(pi.endDate),
                    }
                )
            ) {
                return
            }

            const foundTimesheet = timesheets?.find(
                (t) => t.planItemId === pi._id && isSameWeek(toZonedTime(t.weekStart), week, { weekStartsOn: 1 })
            )

            if (!foundTimesheet || foundTimesheet?.status === "draft" || foundTimesheet?.status === "rejected") {
                if (foundTimesheet) {
                    const appMission = app.state.missions.find(
                        (m) => getObjectId(m) === getObjectId(foundTimesheet.mission)
                    )

                    if (!appMission) {
                        return
                    }

                    /*const orgData = app.state.orgs.find((m) => getObjectId(m) === getObjectId(appMission.org))
                    const role = orgData.roles.find((r) => r._id === pi.role)?.name || pi.role || pi.title
                    console.log("*****************************************************")
                    console.log(week, toZonedTime(pi.startDate), toZonedTime(pi.endDate))
                    console.log("planItem", pi._id, appMission?.title, role, pi.personLeftRole, pi)*/
                } else {
                    /*console.log("++++++++++++++++++++++++++++++++++++++++++++++++++++")
                    console.log(week, toZonedTime(pi.startDate), toZonedTime(pi.endDate))*/
                }

                missing.push(week)
            }
        })
    })

    return missing
}
